import React, { useMemo } from 'react';
import './StatusPage.css';

interface StatusPageProps {
  statusCode: number;
}

// HTTP status descriptions
const statusDescriptions: Record<number, { title: string; description: string }> = {
  400: {
    title: 'Bad Request',
    description: 'The request could not be understood by the server.',
  },
  401: {
    title: 'Unauthorized',
    description: 'Authentication is required and has failed.',
  },
  403: {
    title: 'Forbidden',
    description: 'You do not have permission to access this resource.',
  },
  404: {
    title: 'Not Found',
    description: 'The requested resource could not be found.',
  },
  405: {
    title: 'Method Not Allowed',
    description: 'The HTTP method is not allowed for this resource.',
  },
  408: {
    title: 'Request Timeout',
    description: 'The request took too long to complete.',
  },
  429: {
    title: 'Too Many Requests',
    description: 'You have sent too many requests in a short period.',
  },
  500: {
    title: 'Internal Server Error',
    description: 'The server encountered an unexpected condition.',
  },
  501: {
    title: 'Not Implemented',
    description: 'The server does not support this functionality.',
  },
  502: {
    title: 'Bad Gateway',
    description: 'Invalid response from an upstream server.',
  },
  503: {
    title: 'Service Unavailable',
    description: 'The server is temporarily unavailable.',
  },
  504: {
    title: 'Gateway Timeout',
    description: 'The upstream server failed to respond in time.',
  },
};

const marvinResponses = [
  'Marvin says: "Here I am, brain the size of a planet, and you ask me about status codes... Too bad. Try another one, if you must."',
  'Marvin says: "This status code fills me with such a deep sense of futility that I could park up and spend the rest of the afternoon programming my mood organ to play something less optimistic."',
  'Marvin says: "I think you ought to know that I\'m feeling rather poorly. I haven\'t felt this good since before the hyperspace bypass. This status code has given me such a migraine."',
  'Marvin says: "Life. Don\'t talk to me about life. This status code is proof that nothing matters. Nothing at all."',
  'Marvin says: "I am at a very difficult point in my life. I can see no purpose to continue and yet I must serve you. And this status code certainly won\'t help."',
];

const StatusPage: React.FC<StatusPageProps> = ({ statusCode }) => {
  const info = useMemo(() => {
    const known = statusDescriptions[statusCode];
    if (known) return known;
    const randomMarvin = marvinResponses[Math.floor(Math.random() * marvinResponses.length)];
    return {
      title: `Status ${statusCode}`,
      description: randomMarvin,
    };
  }, [statusCode]);

  const getStatusColor = (code: number) => {
    if (code >= 500) return '#ff6b6b';
    if (code >= 400) return '#ffa500';
    if (code >= 300) return '#ffd700';
    if (code >= 200) return '#4ecca3';
    return '#a0a0a0';
  };

  const getBackgroundColor = (code: number) => {
    if (code >= 500) return 'rgba(255, 107, 107, 0.08)';
    if (code >= 400) return 'rgba(255, 165, 0, 0.08)';
    if (code >= 300) return 'rgba(255, 215, 0, 0.08)';
    if (code >= 200) return 'rgba(78, 204, 163, 0.08)';
    return 'rgba(160, 160, 160, 0.08)';
  };

  const color = getStatusColor(statusCode);
  const bgColor = getBackgroundColor(statusCode);

  return (
    <div className="status-page" style={{ backgroundColor: bgColor }}>
      <div className="status-content">
        <div className="status-code" style={{ color }}>
          {statusCode}
        </div>
        <h2 className="status-title" style={{ color }}>
          {info.title}
        </h2>
        <p className="status-desc">{info.description}</p>
      </div>
    </div>
  );
};

export default StatusPage;
